import { useEffect, useState } from 'react'
import {
  Plus,
  Trash2,
  Edit,
  Globe,
  GlobeLock,
  Star,
  Quote,
  Loader2,
  AlertTriangle,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuthStore } from '@/store/authStore'
import successStoriesService from '@/services/successStoriesService'
import countryHubService from '@/services/countryHubService'
import ImageUploader from '@/components/ImageUploader'
import PageContainer from '@/components/common/PageContainer'
import PageHeader from '@/components/common/PageHeader'
import { useConfirmacion } from '@/hooks/useConfirmacion'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Checkbox } from '@/components/ui/checkbox'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { getInitials } from '@/lib/utils'

const MODULES = [
  { value: 'lexbot', label: 'LexBot (IA Legal)' },
  { value: 'protocolx', label: 'ProtocolX (Notarial)' },
  { value: 'auditpro', label: 'AuditPro (Auditoría)' },
  { value: 'finsync', label: 'FinSync (Finanzas)' },
  { value: 'general', label: 'General' },
]

function emptyForm() {
  return {
    _id: null,
    clientName: '',
    clientRole: '',
    organization: '',
    photoUrl: '',
    quote: '',
    metric: { label: '', value: '' },
    moduleUsed: 'general',
    videoUrl: '',
    isFeatured: false,
    approvedByClient: false,
  }
}

export default function CountrySuccessStories() {
  const { confirmar, dialogo } = useConfirmacion()
  const { partner } = useAuthStore()
  const [items, setItems] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [editorOpen, setEditorOpen] = useState(false)
  const [form, setForm] = useState(emptyForm())

  useEffect(() => {
    load()
  }, [])

  async function load() {
    try {
      setIsLoading(true)
      const res = await successStoriesService.list()
      setItems(res.data || [])
    } catch (e) {
      toast.error(e.response?.data?.message || 'Error al cargar')
    } finally {
      setIsLoading(false)
    }
  }

  function openNew() {
    setForm(emptyForm())
    setEditorOpen(true)
  }

  function openEdit(item) {
    setForm({
      _id: item._id,
      clientName: item.clientName || '',
      clientRole: item.clientRole || '',
      organization: item.organization || '',
      photoUrl: item.photoUrl || '',
      quote: item.quote || '',
      metric: { label: item.metric?.label || '', value: item.metric?.value || '' },
      moduleUsed: item.moduleUsed || 'general',
      videoUrl: item.videoUrl || '',
      isFeatured: !!item.isFeatured,
      approvedByClient: true,
    })
    setEditorOpen(true)
  }

  async function save() {
    if (!form.clientName?.trim() || !form.quote?.trim()) {
      toast.error('Cliente y testimonio son obligatorios')
      return
    }
    if (!form._id && !form.approvedByClient) {
      toast.error('Confirma que el cliente autorizó la publicación')
      return
    }
    try {
      setIsSaving(true)
      if (form._id) {
        await successStoriesService.update(form._id, form)
        toast.success('Caso actualizado')
      } else {
        await successStoriesService.create(form)
        toast.success('Caso creado (no publicado todavía)')
      }
      setEditorOpen(false)
      await load()
    } catch (e) {
      toast.error(e.response?.data?.message || 'Error al guardar')
    } finally {
      setIsSaving(false)
    }
  }

  async function togglePublish(item) {
    const next = !item.isPublished
    try {
      await successStoriesService.publish(item._id, next)
      toast.success(next ? 'Publicado' : 'Despublicado')
      await load()
    } catch (e) {
      toast.error(e.response?.data?.message || 'Error')
    }
  }

  async function toggleFeatured(item) {
    try {
      await successStoriesService.update(item._id, { isFeatured: !item.isFeatured })
      await load()
    } catch (e) {
      toast.error(e.response?.data?.message || 'Error')
    }
  }

  async function remove(item) {
    const ok = await confirmar({
      titulo: 'Eliminar caso de éxito',
      descripcion: `¿Eliminar el caso de «${item.clientName}»? No se puede deshacer.`,
      textoConfirmar: 'Eliminar',
      variante: 'destructiva',
    })
    if (!ok) return
    try {
      await successStoriesService.remove(item._id)
      toast.success('Eliminado')
      await load()
    } catch (e) {
      toast.error(e.response?.data?.message || 'Error')
    }
  }

  if (partner?.organizationType !== 'country_manager') {
    return (
      <PageContainer>
        <Alert variant="warning">
          <AlertTriangle size={16} />
          <AlertTitle>Solo Country Managers</AlertTitle>
          <AlertDescription>
            Solo los Country Managers pueden gestionar casos.
          </AlertDescription>
        </Alert>
      </PageContainer>
    )
  }

  if (isLoading) {
    return (
      <PageContainer>
        <div className="flex justify-center py-12">
          <Loader2 size={28} className="animate-spin text-primary" />
        </div>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <PageHeader
        title="Casos de éxito"
        description="Testimonios reales de profesionales que ya transformaron su práctica con CoreDesk."
        actions={
          <Button onClick={openNew}>
            <Plus size={16} /> Nuevo caso
          </Button>
        }
      />

      {items.length === 0 ? (
        <Card>
          <CardContent className="text-center py-10">
            <h3 className="font-display text-h2 font-semibold text-foreground mb-1">
              Aún no tienes casos de éxito
            </h3>
            <p className="text-body-sm text-subtle mb-4">
              Recopila testimonios de tus clientes para mostrar el impacto real de la
              plataforma.
            </p>
            <Button onClick={openNew}>
              <Plus size={16} /> Crear caso
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {items.map((item) => (
            <Card key={item._id} className="h-full">
              <CardContent className="pt-6">
                <div className="flex flex-wrap gap-1.5 mb-3">
                  <Badge variant={item.isPublished ? 'success' : 'secondary'}>
                    {item.isPublished ? 'Publicado' : 'Borrador'}
                  </Badge>
                  {item.isFeatured && (
                    <Badge variant="warning" className="gap-1">
                      <Star size={11} /> Destacado
                    </Badge>
                  )}
                  <Badge variant="outline">
                    {MODULES.find((m) => m.value === item.moduleUsed)?.label ||
                      item.moduleUsed}
                  </Badge>
                </div>

                <Quote size={20} className="text-neutral-400 dark:text-neutral-500 mb-1" />
                <p className="text-body-sm text-foreground italic mb-4">
                  {item.quote || '(sin cita)'}
                </p>

                {item.metric?.value && (
                  <div className="rounded-md border border-success-100 dark:border-success-800 bg-success-50 dark:bg-success-500/15 p-3 mb-4">
                    <p className="font-display text-h2 font-semibold text-success-700 dark:text-success-300">
                      {item.metric.value}
                    </p>
                    <p className="text-caption text-success-700 dark:text-success-300">{item.metric.label}</p>
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={item.photoUrl} alt={item.clientName} />
                    <AvatarFallback>{getInitials(item.clientName)}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="text-body-sm font-semibold text-foreground truncate">
                      {item.clientName}
                    </p>
                    <p className="text-caption text-subtle truncate">
                      {item.clientRole}
                      {item.organization ? ` · ${item.organization}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-0.5">
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => toggleFeatured(item)}
                      aria-label={item.isFeatured ? 'Quitar destacado' : 'Destacar'}
                      className={item.isFeatured ? 'text-warning' : ''}
                    >
                      <Star
                        size={14}
                        fill={item.isFeatured ? 'currentColor' : 'none'}
                      />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => togglePublish(item)}
                      aria-label={item.isPublished ? 'Despublicar' : 'Publicar'}
                      className={
                        item.isPublished ? 'text-warning' : 'text-success'
                      }
                    >
                      {item.isPublished ? (
                        <GlobeLock size={14} />
                      ) : (
                        <Globe size={14} />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => openEdit(item)}
                      aria-label="Editar"
                    >
                      <Edit size={14} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => remove(item)}
                      aria-label="Eliminar"
                      className="text-danger hover:bg-danger-50 dark:hover:bg-danger-500/15"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {form._id ? 'Editar caso de éxito' : 'Nuevo caso de éxito'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="ss-name">Nombre del cliente</Label>
                <Input
                  id="ss-name"
                  value={form.clientName}
                  onChange={(e) => setForm({ ...form, clientName: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ss-role">Cargo</Label>
                <Input
                  id="ss-role"
                  value={form.clientRole}
                  onChange={(e) => setForm({ ...form, clientRole: e.target.value })}
                  placeholder="ej: Notario público"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ss-org">Organización</Label>
                <Input
                  id="ss-org"
                  value={form.organization}
                  onChange={(e) => setForm({ ...form, organization: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Módulo usado</Label>
                <Select
                  value={form.moduleUsed}
                  onValueChange={(v) => setForm({ ...form, moduleUsed: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MODULES.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ss-quote">Testimonio (cita)</Label>
              <Textarea
                id="ss-quote"
                rows={4}
                value={form.quote}
                onChange={(e) => setForm({ ...form, quote: e.target.value })}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-1.5 sm:col-span-1">
                <Label htmlFor="ss-metric-val">Métrica · Valor</Label>
                <Input
                  id="ss-metric-val"
                  value={form.metric.value}
                  onChange={(e) =>
                    setForm({ ...form, metric: { ...form.metric, value: e.target.value } })
                  }
                  placeholder="60%"
                />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="ss-metric-lbl">Métrica · Label</Label>
                <Input
                  id="ss-metric-lbl"
                  value={form.metric.label}
                  onChange={(e) =>
                    setForm({ ...form, metric: { ...form.metric, label: e.target.value } })
                  }
                  placeholder="Reducción de tiempo"
                />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Foto del cliente</Label>
                <ImageUploader
                  type="profile"
                  currentUrl={form.photoUrl}
                  height={140}
                  circular
                  fallbackName={form.clientName}
                  onUploadRequest={async (file) => {
                    const res = await countryHubService.uploadImage(file, 'profile')
                    return res.data
                  }}
                  onUpload={(data) => setForm({ ...form, photoUrl: data.url })}
                  onDelete={() => setForm({ ...form, photoUrl: '' })}
                />
                <Input
                  placeholder="O URL externa"
                  value={form.photoUrl}
                  onChange={(e) => setForm({ ...form, photoUrl: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ss-video">URL video (YouTube / Vimeo)</Label>
                <Input
                  id="ss-video"
                  value={form.videoUrl}
                  onChange={(e) => setForm({ ...form, videoUrl: e.target.value })}
                />
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <Switch
                checked={form.isFeatured}
                onCheckedChange={(v) => setForm({ ...form, isFeatured: !!v })}
              />
              <span className="text-body-sm">Destacar en carousel principal</span>
            </label>

            {!form._id && (
              <Alert variant="warning">
                <AlertTriangle size={16} />
                <AlertDescription className="space-y-2">
                  <p>
                    Cumplimiento GDPR/Privacidad: necesitas autorización explícita del
                    cliente para publicar su testimonio.
                  </p>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <Checkbox
                      checked={form.approvedByClient}
                      onCheckedChange={(v) =>
                        setForm({ ...form, approvedByClient: !!v })
                      }
                      className="mt-0.5"
                    />
                    <span className="text-body-sm">
                      Confirmo que el cliente autorizó la publicación de este testimonio.
                    </span>
                  </label>
                </AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={save} disabled={isSaving}>
              {isSaving ? 'Guardando…' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {dialogo}
    </PageContainer>
  )
}
